import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Button,
} from '@material-ui/core';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import WorkspaceHeading, { isWorkspacePath } from './WorkspaceHeading';
import logo from '../resources/EntremapLogo.png';
import MenuIcon from '@material-ui/icons/Menu';
import PrivacyModal from './PrivacyModal';

const useStyles = makeStyles((theme) => ({
  appBar: { backgroundColor: '#1e494a', color: 'white' },
  toolbar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: theme.spacing(1, 4),
  },
  logo: { width: 120, height: 40, cursor: 'pointer' },
  navMenu: { display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 },
  navMenu2: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexGrow: 1 },
  link: {
    color: 'white', textDecoration: 'none', marginLeft: theme.spacing(2), marginRight: theme.spacing(0),
    padding: theme.spacing(0.5, 1), borderRadius: '5px', transition: 'background-color 0.3s',
    '&:hover': { backgroundColor: '#022c30' },
  },
  loginButton: {
    backgroundColor: '#F95738', color: 'white', border: 'none',
    padding: theme.spacing(0.5, 1), cursor: 'pointer', borderRadius: '4px',
    '&:hover': { backgroundColor: '#c04e32' },
  },
}));

const NavBar = (props) => {
  const classes = useStyles();
  const location = useLocation();
  const workspacePage = isWorkspacePath(location.pathname);
  const navigationQuery = workspacePage ? '(max-width: 1099px)' : '(max-width: 699px)';
  const publicPage = props.publicPage === true || props.page === "home";
  const activePath = ({home:"/",about:"/about",contact:"/contact",faq:"/faq",privacy:"/privacypolicy"})[props.page];
  const [compactHomeNav, setCompactHomeNav] = useState(() =>
      window.matchMedia(navigationQuery).matches);
  useEffect(() => {
    
    const media = window.matchMedia(navigationQuery);
    const update = () => setCompactHomeNav(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [navigationQuery]);
  const mobileNav = compactHomeNav;

  const [icon, setIcon] = useState(() => localStorage.getItem('userIcon'));
  const [navAnchorEl, setNavAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [qutCohortAllowed, setQutCohortAllowed] = useState(false);

  const allowedEmails = [
    'grahamfellows@gmail.com',
    'ash.knight151203@gmail.com',
    'janmendoza85@gmail.com',
    'lizajane03@outlook.com',
  ];

  // Handle QUT student detection
  useEffect(() => {
    // If parent explicitly passes showCohorts, use that value
    if (typeof props.showCohorts === 'boolean') {
      setQutCohortAllowed(props.showCohorts);
      return;
    }

    // Otherwise, auto-detect QUT student
    const detectQut = async () => {
      if (!props.isLoggedIn) {
        setQutCohortAllowed(false);
        return;
      }

      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        const data = await res.json();
        setQutCohortAllowed(res.ok && data?.success && data?.flags?.isQutStudent === true);
      } catch {
        setQutCohortAllowed(false);
      }
    };

    detectQut();
  }, [props.isLoggedIn, props.showCohorts]);

  useEffect(() => {
    // Avatar
    if (props.isLoggedIn && !icon) {
      fetch('/api/user/icon', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
          .then((r) => r.json())
          .then((data) => {
            setIcon(data.picture);
            localStorage.setItem('userIcon', data.picture);
          })
          .catch(() => {});
    }

    // Admin
    const checkAdmin = async () => {
      try {
        const response = await fetch('/api/user/getCurrentUserEmail', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('User not authenticated');
        const userData = await response.json();
        setIsAdmin(allowedEmails.includes(userData.email));
      } catch (error) {
        console.error('Error checking authorization:', error);
        setIsAdmin(false);
      }
    };
    if (props.isLoggedIn) checkAdmin();
  }, [props.isLoggedIn, icon]);

  const handleNavMenuClick = (e) => setNavAnchorEl(e.currentTarget);
  const handleProfileMenuClick = (e) => setProfileAnchorEl(e.currentTarget);
  const handleNavMenuClose = () => setNavAnchorEl(null);
  const handleProfileMenuClose = () => setProfileAnchorEl(null);

  const handleNavMenu = () => (
      <div>
        <IconButton className={`${classes.link} emap-nav-link`} onClick={handleNavMenuClick} aria-label="Open navigation menu">
          <MenuIcon />
        </IconButton>
        <Menu
            anchorEl={navAnchorEl}
            keepMounted
            open={Boolean(navAnchorEl)}
            onClose={handleNavMenuClose}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            disableScrollLock
        >
          <MenuItem onClick={() => (window.location.href = '/')}>Home</MenuItem>

          <MenuItem onClick={() => (window.location.href = '/about')}>About Us</MenuItem>
          {publicPage && <MenuItem component="a" href="/#how-it-works" onClick={handleNavMenuClose}>How it works</MenuItem>}
          <MenuItem onClick={() => (window.location.href = '/contact')}>Contact</MenuItem>
          <MenuItem onClick={() => (window.location.href = '/faq')}>FAQ</MenuItem>

          {!props.isLoggedIn && <MenuItem component={RouterLink} to="/login">Login</MenuItem>}
          {props.isLoggedIn && <MenuItem component={RouterLink} to="/profile">My profile</MenuItem>}

          {props.isLoggedIn && isAdmin && (
              <MenuItem component={RouterLink} to="/adminDashboard">Admin Dashboard</MenuItem>
          )}
          {/* Cohort only when allowed */}
          {qutCohortAllowed && (
              <MenuItem component={RouterLink} to="/StudentDashboard">Cohorts</MenuItem>
          )}
        </Menu>
      </div>
  );

  const handleMenu = useCallback(() => {
    if (props.isLoggedIn) {
      return (
          <div>
            <IconButton aria-label="Open account menu" aria-haspopup="true" onClick={handleProfileMenuClick}>
              <Avatar alt="User" src={icon || undefined} />
            </IconButton>
            <Menu
                anchorEl={profileAnchorEl}
                keepMounted
                open={Boolean(profileAnchorEl)}
                onClose={handleProfileMenuClose}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                disableScrollLock
            >
              <MenuItem onClick={() => (window.location.href = '/profile')}>Profile</MenuItem>
              <MenuItem
                  onClick={() => {
                    const doLogout = () =>
                        fetch('/api/auth/logout').then((res) => {
                          // Both 200 (successful logout) and 401 (already logged out) should redirect
                          if (res.status === 200 || res.status === 401) {
                            localStorage.removeItem('userIcon');
                            window.location.href = '/';
                          }
                        });
                    if (props.warning) {
                      if (window.confirm('Are you sure you want to sign out?\nSurvey answers will be discarded.')) {
                        doLogout();
                      }
                    } else {
                      doLogout();
                    }
                  }}
              >
                Sign Out
              </MenuItem>
            </Menu>
          </div>
      );
    }
    return null;
  }, [props.isLoggedIn, icon, profileAnchorEl, props.warning]);

  // Desktop pages (Cohort inserted when allowed)
  const pages = [
    { name: 'Home', link: '/' },
    ...(workspacePage && props.isLoggedIn ? [{ name: 'My profile', link: '/profile' }] : []),
    ...(qutCohortAllowed ? [{ name: 'Cohort', link: '/StudentDashboard' }] : []),
    { name: 'About Us', link: '/about' },
    ...(publicPage ? [{ name: 'How it works', link: '/#how-it-works' }] : []),
    { name: 'Contact', link: '/contact' },
    { name: 'FAQ', link: '/faq' },
  ];

  return (
      <>
      <AppBar position="static" className={`${classes.appBar} emap-home-header`}>
        <Toolbar className={`${classes.toolbar} emap-toolbar`}>
          <RouterLink to="/" className={`${classes.logoLink || ""} emap-logo-link`}>
            <img src={logo} alt="Entremap Logo" className={`${classes.logo} emap-logo`} />
          </RouterLink>

          <div className={classes.navMenu}>
            {mobileNav ? (
                <div className={classes.navMenu2}>
                  {handleNavMenu()}
                  {handleMenu()}
                </div>
            ) : (
                <>
                  {pages.map((p) => p.link === '/#how-it-works' ? (
                      <a href="/#how-it-works" key={p.link} className={`${classes.link} emap-nav-link`}>How it works</a>
                  ) : (
                      <RouterLink to={p.link} key={p.link} aria-current={p.link.toLowerCase() === location.pathname.toLowerCase() || (publicPage && p.link === activePath) ? 'page' : undefined} className={`${classes.link} emap-nav-link`}>
                        {p.name}
                      </RouterLink>
                  ))}
                  {props.isLoggedIn && isAdmin && (
                      <RouterLink className={`${classes.link} emap-nav-link`} to="/adminDashboard" aria-current={location.pathname.toLowerCase() === '/admindashboard' ? 'page' : undefined}>Admin dashboard</RouterLink>
                  )}
                  {handleMenu()}
                </>
            )}
          </div>

          {!mobileNav && !props.isLoggedIn && (
              <RouterLink to="/login">
                <Button className={`${classes.loginButton} emap-login-button`}>Login</Button>
              </RouterLink>
          )}
          {publicPage && (
            <div className="emap-header-start">
              {props.isLoggedIn && !icon && <RouterLink className="emap-profile-link" to="/profile">My profile</RouterLink>}
              <PrivacyModal isLoggedIn={props.isLoggedIn} buttonLabel="Get started"
                buttonClassName="emap-button emap-button--small" showArrow />
            </div>
          )}
        </Toolbar>
      </AppBar>
      <WorkspaceHeading />
      </>
  );
};

export default React.memo(NavBar);

